import { useEffect, useState, useMemo } from 'react'
import { ChevronDown, CheckCircle, XCircle, Clock, Loader2, CalendarCheck, ExternalLink, Save, ClipboardList, Eye } from 'lucide-react'
import { attendanceApi, classesApi, studentsApi, leavesApi } from '../api/services.js'
import Avatar from '../components/Avatar.jsx'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

// ── Helpers ───────────────────────────────────────────────────────────────────
const getStudentName = (r) => {
  const s = r.studentId || r.student || r
  if (!s) return null
  if (s.firstName || s.lastName) return `${s.firstName || ''} ${s.lastName || ''}`.trim()
  return s.name || null
}
const getStudentId = (r) => {
  const s = r.studentId || r.student || r
  return s?._id || s?.id || null
}
const getAdmissionNo = (r) => {
  const s = r.studentId || r.student || r
  return s?.admissionNo || r.admissionNo || r.roll || ''
}
const getStudentAvatar = (r) => {
  const s = r.studentId || r.student || r
  return s?.avatar || ''
}
const compareText = (a, b) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })

const today = new Date().toISOString().split('T')[0]

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { key: 'present', label: 'P', full: 'Present',  color: 'bg-green-50  text-green-700  border-green-200',   dot: 'bg-green-500',  active: 'bg-green-600  text-white border-green-600'  },
  { key: 'absent',  label: 'A', full: 'Absent',   color: 'bg-red-50    text-red-600    border-red-200',     dot: 'bg-red-500',    active: 'bg-red-600    text-white border-red-600'    },
  { key: 'leave',   label: 'L', full: 'Leave',    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',  dot: 'bg-yellow-500', active: 'bg-yellow-500 text-white border-yellow-500' },
]
const statusMeta = (key) => STATUS_OPTIONS.find((s) => s.key === key) || STATUS_OPTIONS[0]

export default function AttendancePage() {
  const navigate = useNavigate()

  // ── Tab state ───────────────────────────────────────────────────────────────
  const [tab, setTab] = useState('mark') // 'mark' | 'view'

  // ── Shared filters ──────────────────────────────────────────────────────────
  const [classOptions, setClassOptions] = useState([])
  const [selectedClass, setSelectedClass] = useState('')
  const [selectedDate, setSelectedDate] = useState(today)
  const [loadingClasses, setLoadingClasses] = useState(true)

  // ── Mark-tab state ──────────────────────────────────────────────────────────
  const [students, setStudents] = useState([])
  const [loadingStudents, setLoadingStudents] = useState(false)
  const [attendance, setAttendance] = useState({}) // { studentId → 'present' | 'absent' | 'leave' }
  const [saving, setSaving] = useState(false)
  const [sortBy, setSortBy] = useState('name')    // 'name' | 'admissionNo'
  const [existingRecords, setExistingRecords] = useState(null) // null=not loaded, []= no records

  // ── View-tab state ──────────────────────────────────────────────────────────
  const [records, setRecords] = useState([])
  const [loadingRecords, setLoadingRecords] = useState(false)

  // ── Load classes ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoadingClasses(true)
        const data = await classesApi.getAll()
        const list = Array.isArray(data) ? data : data?.classes ?? []
        setClassOptions(list)
        if (list.length > 0) setSelectedClass(list[0]._id || list[0].id)
      } catch { toast.error('Failed to load classes') }
      finally { setLoadingClasses(false) }
    }
    fetchClasses()
  }, [])

  // ── Load students + existing attendance (mark tab) ───────────────────────────
  useEffect(() => {
    if (!selectedClass || tab !== 'mark') return
    const fetchForMarking = async () => {
      try {
        setLoadingStudents(true)
        const [studRes, attRes, leaveRes] = await Promise.allSettled([
          studentsApi.getAll({ classId: selectedClass }),
          attendanceApi.getByClass(selectedClass, selectedDate),
          leavesApi.getAll({ status: 'Approved', classId: selectedClass, date: selectedDate }).catch(() => []),
        ])

        const studentList = studRes.status === 'fulfilled'
          ? (Array.isArray(studRes.value) ? studRes.value : studRes.value?.users ?? [])
          : []
        setStudents(studentList)

        const existing = attRes.status === 'fulfilled'
          ? (Array.isArray(attRes.value) ? attRes.value : attRes.value?.records ?? attRes.value?.attendance ?? [])
          : []
        setExistingRecords(existing)

        // Collect approved-leave student IDs
        const onLeaveIds = new Set()
        if (leaveRes.status === 'fulfilled') {
          const leaves = Array.isArray(leaveRes.value) ? leaveRes.value : leaveRes.value?.leaves ?? []
          leaves.forEach((l) => {
            const sid = l.userId?._id || l.userId || l.studentId?._id || l.studentId
            if (sid) onLeaveIds.add(String(sid))
          })
        }

        // Build attendance map: existing > approved-leave > default-present
        const map = {}
        studentList.forEach((s) => {
          const sid = String(s._id || s.id)
          const rec = existing.find((r) => {
            const rid = getStudentId(r)
            return rid && String(rid) === sid
          })
          if (rec) {
            map[sid] = rec.status
          } else if (onLeaveIds.has(sid)) {
            map[sid] = 'leave'
          } else {
            map[sid] = 'present'
          }
        })
        setAttendance(map)
      } catch {
        toast.error('Failed to load class data')
        setStudents([])
      } finally {
        setLoadingStudents(false)
      }
    }
    fetchForMarking()
  }, [selectedClass, selectedDate, tab])

  // ── Load attendance records (view tab) ────────────────────────────────────────
  useEffect(() => {
    if (!selectedClass || tab !== 'view') return
    const fetchRecords = async () => {
      try {
        setLoadingRecords(true)
        const data = await attendanceApi.getByClass(selectedClass, selectedDate)
        setRecords(Array.isArray(data) ? data : data?.records ?? data?.attendance ?? [])
      } catch { toast.error('Failed to load records'); setRecords([]) }
      finally { setLoadingRecords(false) }
    }
    fetchRecords()
  }, [selectedClass, selectedDate, tab])

  // ── Sorted students ───────────────────────────────────────────────────────────
  const sortedStudents = useMemo(() => [...students].sort((a, b) => {
    if (sortBy === 'admissionNo') return compareText(a.admissionNo || '', b.admissionNo || '')
    const aName = a.name || `${a.firstName || ''} ${a.lastName || ''}`.trim()
    const bName = b.name || `${b.firstName || ''} ${b.lastName || ''}`.trim()
    return compareText(aName, bName)
  }), [students, sortBy])

  // ── Summary counts ────────────────────────────────────────────────────────────
  const markStats = useMemo(() => {
    const vals = Object.values(attendance)
    return {
      present: vals.filter((v) => v === 'present').length,
      absent:  vals.filter((v) => v === 'absent').length,
      leave:   vals.filter((v) => v === 'leave').length,
    }
  }, [attendance])

  const viewStats = {
    present: records.filter((r) => r.status === 'present').length,
    absent:  records.filter((r) => r.status === 'absent').length,
    late:    records.filter((r) => r.status === 'late').length,
  }

  const isFutureDate = selectedDate > today

  // ── Save bulk attendance ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (isFutureDate) { toast.error('Cannot mark attendance for a future date'); return }
    if (students.length === 0) { toast.error('No students found in this class'); return }

    const payload = students.map((s) => ({
      studentId: s._id || s.id,
      status: attendance[String(s._id || s.id)] || 'present',
    }))

    try {
      setSaving(true)
      await attendanceApi.saveBulk({ classId: selectedClass, date: selectedDate, attendance: payload })
      toast.success(existingRecords?.length ? 'Attendance updated' : 'Attendance saved')
      setExistingRecords(payload) // mark as "existing" after save
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save attendance')
    } finally {
      setSaving(false)
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const classLabel = (c) => c.section ? `${c.name} — ${c.section}` : c.name
  const selectedClassObj = classOptions.find((c) => (c._id || c.id) === selectedClass)

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Attendance</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {tab === 'mark' ? 'Mark daily attendance for a class' : 'View saved attendance records'}
          </p>
        </div>
        {tab === 'mark' && (
          <button
            onClick={handleSave}
            disabled={saving || loadingStudents || students.length === 0 || isFutureDate}
            className="btn-primary flex items-center gap-2 self-start sm:self-auto disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {existingRecords?.length ? 'Update Attendance' : 'Save Attendance'}
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {[
          { key: 'mark', label: 'Mark Attendance', icon: ClipboardList },
          { key: 'view', label: 'View Records',    icon: Eye },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Filters row */}
      <div className="card p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        {/* Class picker */}
        <div className="relative flex-1">
          {loadingClasses ? (
            <div className="input-field w-full flex items-center gap-2 text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading classes…
            </div>
          ) : (
            <>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="input-field w-full pr-8 appearance-none cursor-pointer"
              >
                {classOptions.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>{classLabel(c)}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </>
          )}
        </div>

        {/* Date picker */}
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          max={today}
          className="input-field flex-1"
        />

        {/* Sort options (mark tab only) */}
        {tab === 'mark' && (
          <div className="flex gap-1">
            {[{ key: 'name', label: 'Name' }, { key: 'admissionNo', label: 'Adm No' }].map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`text-xs px-3 py-2 rounded-lg font-medium border transition-colors ${
                  sortBy === s.key
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Future-date warning */}
      {isFutureDate && tab === 'mark' && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-700">
          <CalendarCheck className="w-4 h-4 flex-shrink-0" />
          Attendance cannot be marked for a future date. Please select today or a past date.
        </div>
      )}

      {/* ─────────────────── MARK ATTENDANCE TAB ─────────────────────── */}
      {tab === 'mark' && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Present', count: markStats.present, color: 'text-green-600', bg: 'bg-green-50',  icon: CheckCircle },
              { label: 'Absent',  count: markStats.absent,  color: 'text-red-500',   bg: 'bg-red-50',    icon: XCircle },
              { label: 'Leave',   count: markStats.leave,   color: 'text-yellow-600',bg: 'bg-yellow-50', icon: Clock },
            ].map(({ label, count, color, bg, icon: Icon }) => (
              <div key={label} className="card p-4 text-center">
                <div className={`w-8 h-8 ${bg} rounded-full flex items-center justify-center mx-auto mb-2`}>
                  <Icon className={`w-4 h-4 ${color}`} />
                </div>
                <p className={`text-2xl font-extrabold ${color}`}>{loadingStudents ? '—' : count}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Student list */}
          <div className="card overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">
                {selectedClassObj ? classLabel(selectedClassObj) : 'Class'} — {selectedDate}
              </h3>
              <div className="flex items-center gap-3">
                {existingRecords?.length > 0 && (
                  <span className="text-xs bg-blue-50 text-blue-600 font-medium px-2.5 py-1 rounded-full">
                    Editing existing record
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  {loadingStudents ? '…' : `${students.length} students`}
                </span>
              </div>
            </div>

            {loadingStudents ? (
              <div className="divide-y divide-gray-50">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 px-5 py-4 animate-pulse">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-4 bg-gray-100 rounded w-1/3" />
                      <div className="h-3 bg-gray-100 rounded w-1/4" />
                    </div>
                    <div className="flex gap-1.5">
                      {[0,1,2].map((j) => <div key={j} className="w-9 h-9 bg-gray-100 rounded-lg" />)}
                    </div>
                  </div>
                ))}
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-14 text-gray-400">
                <CalendarCheck className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                <p className="text-sm">No students found in this class.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {sortedStudents.map((student, i) => {
                  const sid = String(student._id || student.id)
                  const name = student.name || `${student.firstName || ''} ${student.lastName || ''}`.trim()
                  const admNo = student.admissionNo || ''
                  const currentStatus = attendance[sid] || 'present'
                  const meta = statusMeta(currentStatus)

                  return (
                    <div key={sid} className="flex items-center gap-4 px-5 py-3">
                      <span className="text-xs text-gray-400 w-6 flex-shrink-0 text-right">{i + 1}</span>

                      <Avatar src={student.avatar} name={name || 'Student'} size="sm" />

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{name || 'Unknown'}</p>
                        {admNo && <p className="text-xs text-gray-400">Adm. {admNo}</p>}
                      </div>

                      {/* Current status pill (desktop) */}
                      <span className={`hidden sm:flex text-xs font-medium px-2.5 py-1 rounded-full border items-center gap-1.5 flex-shrink-0 ${meta.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                        {meta.full}
                      </span>

                      {/* P / A / L toggle buttons */}
                      <div className="flex gap-1 flex-shrink-0">
                        {STATUS_OPTIONS.map((opt) => (
                          <button
                            key={opt.key}
                            onClick={() => setAttendance((prev) => ({ ...prev, [sid]: opt.key }))}
                            disabled={isFutureDate}
                            title={opt.full}
                            className={`w-9 h-9 rounded-lg text-xs font-bold border transition-all disabled:opacity-40 ${
                              currentStatus === opt.key
                                ? opt.active
                                : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300 hover:text-gray-600'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      {/* Profile link */}
                      <button
                        onClick={() => navigate(`/users/${sid}`)}
                        className="p-1 text-gray-300 hover:text-blue-500 transition-colors flex-shrink-0"
                        title="View profile"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Bottom save bar */}
            {students.length > 0 && !loadingStudents && (
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                <p className="text-xs text-gray-500">
                  {markStats.present}P · {markStats.absent}A · {markStats.leave}L
                </p>
                <button
                  onClick={handleSave}
                  disabled={saving || isFutureDate}
                  className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {existingRecords?.length ? 'Update' : 'Save'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ─────────────────── VIEW RECORDS TAB ────────────────────────── */}
      {tab === 'view' && (
        <>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Present', count: viewStats.present, color: 'text-green-600', bg: 'bg-green-50',  icon: CheckCircle },
              { label: 'Absent',  count: viewStats.absent,  color: 'text-red-500',   bg: 'bg-red-50',    icon: XCircle },
              { label: 'Late',    count: viewStats.late,    color: 'text-yellow-600',bg: 'bg-yellow-50', icon: Clock },
            ].map(({ label, count, color, bg, icon: Icon }) => (
              <div key={label} className="card p-4 text-center">
                <div className={`w-8 h-8 ${bg} rounded-full flex items-center justify-center mx-auto mb-2`}>
                  <Icon className={`w-4 h-4 ${color}`} />
                </div>
                <p className={`text-2xl font-extrabold ${color}`}>{loadingRecords ? '—' : count}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          <div className="card overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">
                {selectedClassObj ? classLabel(selectedClassObj) : 'Class'} — {selectedDate}
              </h3>
              <span className="text-xs text-gray-400">
                {loadingRecords ? '…' : `${records.length} students`}
              </span>
            </div>

            {loadingRecords ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : records.length === 0 ? (
              <div className="text-center py-14 text-gray-400">
                <CalendarCheck className="w-10 h-10 mx-auto mb-3 text-gray-200" />
                <p className="text-sm">No attendance records for this date.</p>
                <p className="text-xs mt-1 text-gray-300">Use "Mark Attendance" to add records.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {records.map((r, i) => {
                  const status = (r.status || 'present').toLowerCase()
                  const meta   = statusMeta(status)
                  const name   = getStudentName(r)
                  const sid    = getStudentId(r)
                  const admNo  = getAdmissionNo(r)
                  return (
                    <div key={r._id || r.id || i} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors group">
                      <span className="text-xs text-gray-400 w-6 flex-shrink-0 text-right">{i + 1}</span>
                      <Avatar src={getStudentAvatar(r)} name={name || 'Student'} size="sm" />
                      <div className="flex-1 min-w-0">
                        {name ? (
                          <button
                            onClick={() => sid && navigate(`/users/${sid}`)}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline text-left truncate block transition-colors"
                          >
                            {name}
                          </button>
                        ) : (
                          <p className="text-sm font-medium text-gray-400 italic">Unknown student</p>
                        )}
                        {admNo && <p className="text-xs text-gray-400">Adm. {admNo}</p>}
                      </div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 flex-shrink-0 border ${meta.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                        {meta.full}
                      </span>
                      {sid && (
                        <button
                          onClick={() => navigate(`/users/${sid}`)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-300 hover:text-blue-500"
                          title="View profile"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
